import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import {
  createInfraFacility,
  deleteInfraFacility,
  fetchDetailInfraFacility,
  fetchInfraFacilityList,
  updateInfraFacility,
} from '@/domains/asset-management/apis/infra-facility-management';
import type {
  CreateInfraFacilityPayload,
  CreateInfraFacilityResponse,
  DeleteInfraFacilityResponse,
  InfraFacilityDetailResponse,
  InfraFacilityID,
  InfraFacilityListResponse,
  UpdateInfraFacilityParams,
  UpdateInfraFacilityPayload,
  UpdateInfraFacilityResponse,
  UseInfraFacilityManagementProps,
} from '@/domains/asset-management/types/infra-facility';
import NoticeMessage from '@/plugin/noticemessage/noticemessage';

/** Shared by InfraManagement.tsx and FacilityManagement.tsx - both screens are
 * filtered views of the same infra_and_facility table/endpoint, distinguished
 * only by the fixed `infra_and_facility_type` baked into each page's queryParams. */
const useInfraFacilityManagement = ({
  infraFacilityId = null,
  queryParams = '',
  onDeleteSuccess = () => {},
  onCreateSuccess = () => {},
  onUpdateSuccess = () => {},
  refetchInterval,
}: UseInfraFacilityManagementProps) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { data: infraFacilityListData, isLoading: isLoadingInfraFacilityList } =
    useQuery<InfraFacilityListResponse>({
      queryKey: ['infraFacilityListData', queryParams],
      queryFn: () => fetchInfraFacilityList(queryParams),
      placeholderData: keepPreviousData,
      staleTime: 1000 * 60 * 1,
      refetchInterval: refetchInterval,
      refetchIntervalInBackground: true,
      initialData: {
        code: '-',
        message: '-',
        data: { total: 0, page: 1, size: 10, total_pages: 0, rows: [] },
      },
      initialDataUpdatedAt: 0,
    });

  const { data: detailInfraFacilityData, refetch: refetchDetailInfraFacility } = useQuery<
    InfraFacilityDetailResponse,
    Error
  >({
    queryKey: ['detailInfraFacilityData', infraFacilityId],
    queryFn: () => fetchDetailInfraFacility(infraFacilityId as InfraFacilityID),
    retry: false,
    enabled: !!infraFacilityId,
  });

  const createInfraFacilityMutation = useMutation<
    CreateInfraFacilityResponse,
    Error,
    CreateInfraFacilityPayload
  >({
    mutationFn: (data) => createInfraFacility(data),
    onSuccess: (responseData) => {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('msg > registration success'), {
        callback() {
          queryClient.invalidateQueries({ queryKey: ['infraFacilityListData', queryParams] });
          onCreateSuccess(responseData.data);
        },
      });
    },
    onError: (error: unknown) => {
      const axiosError = error as AxiosError<{ message: string }>;
      const message = axiosError.response?.data?.message || axiosError.message;
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t(message));
    },
  });

  const deleteInfraFacilityMutation = useMutation<
    DeleteInfraFacilityResponse,
    Error,
    InfraFacilityID
  >({
    mutationFn: (id) => deleteInfraFacility(id),
    onSuccess: () => {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('msg > delete success'), {
        callback() {
          onDeleteSuccess();
          queryClient.invalidateQueries({ queryKey: ['infraFacilityListData', queryParams] });
        },
      });
    },
    onError: (error: unknown) => {
      const axiosError = error as AxiosError<{ message: string }>;
      const message = axiosError.response?.data?.message || axiosError.message;
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t(message));
    },
  });

  const updateInfraFacilityMutation = useMutation<
    UpdateInfraFacilityResponse,
    Error,
    { infraFacilityData: UpdateInfraFacilityPayload; params: UpdateInfraFacilityParams }
  >({
    mutationFn: ({ infraFacilityData, params }) => updateInfraFacility(infraFacilityData, params),
    onSuccess: (responseData) => {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('msg > update success'), {
        callback() {
          queryClient.invalidateQueries({ queryKey: ['infraFacilityListData', queryParams] });
          onUpdateSuccess(responseData.data);
        },
      });
    },
    onError: (error: unknown) => {
      const axiosError = error as AxiosError<{ message: string }>;
      const message = axiosError.response?.data?.message || axiosError.message;
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t(message));
    },
  });

  return {
    infraFacilityListData,
    isLoadingInfraFacilityList,
    detailInfraFacilityData,
    refetchDetailInfraFacility,
    createInfraFacility: createInfraFacilityMutation.mutate,
    updateInfraFacility: updateInfraFacilityMutation.mutate,
    deleteInfraFacility: deleteInfraFacilityMutation.mutate,
  };
};

export default useInfraFacilityManagement;
